import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, Trash2, User } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CommentSection = ({ articleId }) => {
  const { user, loading: authLoading } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [articleId]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/comments/${articleId}`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/comments`, {
        article_id: articleId,
        content: newComment.trim()
      });
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment posted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  return (
    <section className="mt-12" data-testid="comment-section">
      <h3 className="font-serif text-xl font-medium mb-6 flex items-center space-x-2">
        <MessageSquare className="h-5 w-5" />
        <span>Discussion ({comments.length})</span>
      </h3>

      {/* Comment Form */}
      {authLoading ? (
        <div className="mb-8 p-6 bg-slate-50 border border-slate-200">
          <div className="skeleton h-4 w-48 mb-2"></div>
          <div className="skeleton h-24 w-full"></div>
        </div>
      ) : user ? (
        <form onSubmit={handleSubmit} className="mb-8" data-testid="comment-form">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Share your thoughts..."
            className="mb-4 min-h-[100px] resize-none border-slate-200 focus:border-slate-900 rounded-none"
            data-testid="comment-input"
          />
          <Button 
            type="submit" 
            disabled={submitting || !newComment.trim()}
            className="btn-primary"
            data-testid="comment-submit"
          >
            {submitting ? 'Posting...' : 'Post Comment'}
          </Button>
        </form>
      ) : (
        <div className="mb-8 p-6 bg-slate-50 border border-slate-200" data-testid="login-prompt">
          <p className="text-sm text-slate-600 mb-4">
            Join the discussion. Login or register to leave a comment.
          </p>
          <div className="flex space-x-3">
            <Link to="/login">
              <Button variant="outline" className="text-sm">Login</Button>
            </Link>
            <Link to="/register">
              <Button className="btn-primary text-xs">Register</Button>
            </Link>
          </div>
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-0">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="p-4 border-b border-slate-100">
                <div className="skeleton h-4 w-24 mb-2"></div>
                <div className="skeleton h-16 w-full"></div>
              </div>
            ))}
          </div>
        ) : comments.length === 0 ? (
          <p className="text-slate-500 text-sm py-8 text-center">
            No comments yet. Be the first to share your thoughts.
          </p>
        ) : (
          comments.map((comment) => (
            <div 
              key={comment.id} 
              className="comment-card animate-fadeIn"
              data-testid={`comment-${comment.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-6 h-6 bg-slate-900 text-white flex items-center justify-center text-xs">
                    <User className="h-3 w-3" />
                  </div>
                  <span className="font-medium text-sm">{comment.username}</span>
                  <span className="text-xs text-slate-400 font-mono">
                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                  </span>
                </div>
                {user && user.id === comment.user_id && (
                  <button 
                    onClick={() => handleDelete(comment.id)}
                    className="text-slate-400 hover:text-red-600 p-1"
                    data-testid={`delete-comment-${comment.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="text-slate-700 text-sm leading-relaxed pl-8">
                {comment.content}
              </p>
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default CommentSection;
